import { useCallback, useMemo } from 'react';
import { InvestmentMemoFormData, FieldPath, SelectedProFormaOption } from '../types';
import { getEmptyRequiredFields } from '../helpers';
import { CARD_REQUIRED_FIELDS, NAV_ITEM_CARDS, InvestmentMemoNavItem } from '../constants';

export const useValidation = (
    formData: InvestmentMemoFormData,
    selectedProForma?: SelectedProFormaOption,
    hasAttemptedGeneration?: boolean,
) => {
    const emptyRequiredFields = useMemo(
        () => getEmptyRequiredFields(formData),
        [formData],
    );

    const shouldShowError = useCallback((fieldPath: FieldPath): boolean => (
        !!selectedProForma && !!hasAttemptedGeneration && emptyRequiredFields.has(fieldPath)
    ), [selectedProForma, hasAttemptedGeneration, emptyRequiredFields]);

    const getFieldError = useCallback((fieldPath: FieldPath): string | null => (
        shouldShowError(fieldPath) ? 'This field is required' : null
    ), [shouldShowError]);

    const hasSectionError = useCallback((cardId: string): boolean => {
        if (!selectedProForma || !hasAttemptedGeneration) return false;
        const fields = CARD_REQUIRED_FIELDS[cardId] || [];
        return fields.some(field => emptyRequiredFields.has(field));
    }, [selectedProForma, hasAttemptedGeneration, emptyRequiredFields]);

    const hasNavError = useCallback((navItem: InvestmentMemoNavItem): boolean => {
        if (!selectedProForma || !hasAttemptedGeneration) return false;
        const cardIds = NAV_ITEM_CARDS[navItem] || [];
        return cardIds.some(cardId => hasSectionError(cardId));
    }, [selectedProForma, hasAttemptedGeneration, hasSectionError]);

    return {
        shouldShowError,
        getFieldError,
        hasSectionError,
        hasNavError,
    };
};
